import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { PopoverController } from '@ionic/angular/standalone';
import { AbnormalFlagIndicatorComponent } from 'src/app/components/abnormal-flag-indicator/abnormal-flag-indicator.component';
import { ItemInputComponent } from 'src/app/components/item-input/item-input.component';
import { ItemLabelComponent } from 'src/app/components/item-label/item-label.component';
import { ItemValueComponent } from 'src/app/components/item-value/item-value.component';
import { ItemComponent } from 'src/app/components/item/item.component';
import { NavigableDirective } from 'src/app/directives/navigable.directive';
import { AbnormalFlagLabelPipe } from 'src/app/pipes/abnormal-flag-label.pipe';
import { AbnormalFlag } from 'src/app/types/sheet-action-aggregate-graph.types';

export interface UnitQuantityResult {
	unit: string | null;
	quantity: number | null;
	abnormalFlag: AbnormalFlag | null;
	range?: string | null; // added after the initial version of Blood Gas Results, hence being optional here
}

@Component({
	selector: 'app-unit-quantity-result',
	templateUrl: './unit-quantity-result.control.html',
	styleUrl: './unit-quantity-result.control.scss',
	imports: [ReactiveFormsModule, ItemComponent, ItemLabelComponent, ItemInputComponent, ItemValueComponent, AbnormalFlagIndicatorComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: UnitQuantityResultControl,
		multi: true
	}]
})
export class UnitQuantityResultControl implements ControlValueAccessor, OnInit {

	@Input() readOnly: boolean = false;
	@Input() label: string = '';
	@Input() defaultUnit: string | null = null;
	@Input() placeholder: string = '';
	@Input() maxlength: number | null = null;
	@Input() inputMode: 'numeric' | 'decimal' | null = null;

	@ViewChild(ItemInputComponent) itemInputComponent!: ItemInputComponent;

	private unitQuantityResult!: UnitQuantityResult;

	get quantity() { return this.unitQuantityResult.quantity?.toString() ?? ''; }
	get unit() { return this.unitQuantityResult.unit ?? this.defaultUnit; }
	get abnormalFlag() { return this.unitQuantityResult.abnormalFlag; }
	get highlightClass() {
		if (this.abnormalFlag === null) return 'unknown';
		switch (this.abnormalFlag) {
			case AbnormalFlag.BelowNormalRange:
			case AbnormalFlag.BelowCriticalRange:
			case AbnormalFlag.BelowMeasurableRange:
				return 'low';
			case AbnormalFlag.AboveNormalRange:
			case AbnormalFlag.AboveCriticalRange:
			case AbnormalFlag.AboveMeasurableRange:
				return 'high';
			default:
				return '';
		}
	}
	get range() { return this.unitQuantityResult?.range; }

	constructor(private changeDetectorRef: ChangeDetectorRef, private popoverController: PopoverController) {
	}

	ngOnInit(): void {
		if (this.inputMode === null) throw new Error('The inputMode must be specified');
		this.unitQuantityResult = this.getDefaultValue();
	}

	writeValue(value: UnitQuantityResult | null): void {
		this.unitQuantityResult = structuredClone(value) ?? this.getDefaultValue();

		// this is required for the view to update correctly (it's a general approach recommended by Angular devs using ChangeDetectionStrategy.OnPush: https://github.com/angular/angular/issues/21780)
		this.changeDetectorRef.markForCheck();
	}

	private getDefaultValue() {
		return { unit: this.defaultUnit, quantity: null, abnormalFlag: null } as UnitQuantityResult;
	}

	private onChange: (value: UnitQuantityResult) => void = () => { };

	registerOnChange(fn: (value: UnitQuantityResult) => void): void {
		this.onChange = fn;
	}

	private onTouched: () => void = () => { };

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(disabled: boolean): void {
	}

	quantityChanged(quantity: string | null) {

		this.onTouched();

		if (quantity === null || quantity === '') {
			this.unitQuantityResult.quantity = null;
		}
		else { // it should be a number ('numeric' or 'decimal')
			const numberValue = this.inputMode === 'numeric' ? parseInt(quantity) : parseFloat(quantity);
			if (isNaN(numberValue)) throw new Error(`Error converting value (${quantity}) for input mode (${this.inputMode})`);
			this.unitQuantityResult.quantity = numberValue;
		}
		this.onChange(structuredClone(this.unitQuantityResult));
	}

	itemFocused() {
		this.onTouched();
	}

	controlClicked() {
		if (!this.readOnly) this.itemInputComponent.focusInput();
	}

	async presentAbnormalFlagPopover(event: Event) {
		if (this.readOnly) return;

		this.onTouched();

		const popover = await this.popoverController.create({
			component: AbnormalFlagPopoverComponent,
			cssClass: 'popover-menu',
			event: event,
			alignment: 'end',
			showBackdrop: false
		});

		await popover.present();

		const { data } = await popover.onWillDismiss();

		if (!data) return;

		const flag = data.flag as AbnormalFlag;

		if (this.unitQuantityResult.abnormalFlag === flag) return;

		this.unitQuantityResult.abnormalFlag = flag;

		this.onChange(structuredClone(this.unitQuantityResult));
		this.changeDetectorRef.markForCheck();
	}
}

@Component({
	template: `
<div class="popover-menu-content">
	<app-item appNavigable (click)="flagSelected(AbnormalFlag.AboveMeasurableRange)">
		<app-item-label>{{ AbnormalFlag.AboveMeasurableRange | abnormalFlagLabel }}</app-item-label><app-abnormal-flag-indicator class="rounded-border-box high" [flag]="AbnormalFlag.AboveMeasurableRange"></app-abnormal-flag-indicator>
	</app-item>
	<app-item underlined appNavigable (click)="flagSelected(AbnormalFlag.AboveCriticalRange)">
		<app-item-label>{{ AbnormalFlag.AboveCriticalRange | abnormalFlagLabel }}</app-item-label><app-abnormal-flag-indicator class="rounded-border-box high" [flag]="AbnormalFlag.AboveCriticalRange"></app-abnormal-flag-indicator>
	</app-item>
	<app-item underlined appNavigable (click)="flagSelected(AbnormalFlag.AboveNormalRange)">
		<app-item-label>{{ AbnormalFlag.AboveNormalRange | abnormalFlagLabel }}</app-item-label><app-abnormal-flag-indicator class="rounded-border-box high" [flag]="AbnormalFlag.AboveNormalRange"></app-abnormal-flag-indicator>
	</app-item>
	<app-item underlined appNavigable (click)="flagSelected(AbnormalFlag.Normal)">
		<app-item-label>{{ AbnormalFlag.Normal | abnormalFlagLabel }}</app-item-label><app-abnormal-flag-indicator class="rounded-border-box" [flag]="AbnormalFlag.Normal"></app-abnormal-flag-indicator>
	</app-item>
	<app-item underlined appNavigable (click)="flagSelected(AbnormalFlag.BelowNormalRange)">
		<app-item-label>{{ AbnormalFlag.BelowNormalRange | abnormalFlagLabel }}</app-item-label><app-abnormal-flag-indicator class="rounded-border-box low" [flag]="AbnormalFlag.BelowNormalRange"></app-abnormal-flag-indicator>
	</app-item>
	<app-item underlined appNavigable (click)="flagSelected(AbnormalFlag.BelowCriticalRange)">
		<app-item-label>{{ AbnormalFlag.BelowCriticalRange | abnormalFlagLabel }}</app-item-label><app-abnormal-flag-indicator class="rounded-border-box low" [flag]="AbnormalFlag.BelowCriticalRange"></app-abnormal-flag-indicator>
	</app-item>
	<app-item underlined appNavigable (click)="flagSelected(AbnormalFlag.BelowMeasurableRange)">
		<app-item-label>{{ AbnormalFlag.BelowMeasurableRange | abnormalFlagLabel }}</app-item-label><app-abnormal-flag-indicator class="rounded-border-box low" [flag]="AbnormalFlag.BelowMeasurableRange"></app-abnormal-flag-indicator>
	</app-item>
	<app-item appNavigable (click)="flagSelected(null)">
		<app-item-label>Not recorded</app-item-label><app-abnormal-flag-indicator class="rounded-border-box unknown"></app-abnormal-flag-indicator>
	</app-item>
</div>`,
	styles: `
app-abnormal-flag-indicator {
	display: block;
	padding: calc(var(--item-vertical-spacing) / 2) 0;
	min-width: 52px;
	text-align: center
}
.low {
	border-color: var(--color-other-1)
}

.high {
	border-color: var(--color-critical-1)
}

.unknown {
	border-color: var(--color-warning-1)
}
`,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [ItemComponent, NavigableDirective, ItemLabelComponent, AbnormalFlagLabelPipe, AbnormalFlagIndicatorComponent]
})
export class AbnormalFlagPopoverComponent {

	AbnormalFlag = AbnormalFlag;

	constructor(private popoverController: PopoverController) {
	}

	flagSelected(flag: AbnormalFlag | null) {
		this.popoverController.dismiss({ flag });
	}
} 